import LoyaltyChooseDoubleRewardsOfferPill from 'takeovers/loyaltyChooseDoubleRewardsTakeover/components/LoyaltyChooseDoubleRewardsOfferPill'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import TourV2Itinerary from 'components/OfferPage/TourV2/Itinerary/TourV2Itinerary'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { useAppSelector } from 'hooks/reduxHooks'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import { getRoomTypeCapacity, getTourV2VariationDuration } from 'lib/tours/tourUtils'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { last, sortBy } from 'lib/array/arrayUtils'
import useSearchOfferBurnPricing from 'hooks/useSearchOfferBurnPricing'
import useRebrandVersion from 'hooks/useRebrandVersion'
import { getTourV2OperatorDisplayName } from 'components/OfferPage/TourV2/tourV2OperatorRebrand'

interface Props {
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  variation?: App.Tours.TourV2OfferVariation
  filters?: App.OfferListFilters
}

function TourV2SearchTileMeta({ offer, variation, filters }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const isRebrandEnabled = useRebrandVersion() === 'rebrand'

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
  })

  const list = useMemo<Array<string>>(() => {
    if (variation) {
      return [
        intl.formatMessage(
          {
            id: 'CtbgI-KZknDrp_k_dSf0',
            defaultMessage: '{count, plural, one {# day} other {# days}}',
            description: 'Number of days in tour variation duration on the tour page',
          },
          { count: getTourV2VariationDuration(variation) },
        ),
        intl.formatMessage(
          {
            id: 'PX6f4xHC7jLGkH6GZ4_1',
            defaultMessage: '{count, plural, one {# place} other {# places}}',
            description: 'Number of places visited in tour variation on the tour page',
          },
          { count: variation.placesVisited.length },
        ),
        intl.formatMessage(
          {
            id: 'eT8sMsht9hrSU4IZfmyc',
            defaultMessage: '{count, plural, one {# country} other {# countries}}',
            description: 'Number of countries visited in tour variation in the tours section',
          },
          { count: variation.countriesVisited.length },
        ),
      ]
    }
    return []
  }, [variation, intl])

  const { firstDepartureDate, lastDepartureDate } = useMemo<{ firstDepartureDate: string, lastDepartureDate: string }>(() => {
    const sortedDepartures = sortBy(variation?.departures ?? [], (departure) => departure.startDate, 'asc')
    const firstDepartureDate = sortedDepartures[0].startDate
    const lastDepartureDate = last(sortedDepartures).startDate
    return { firstDepartureDate, lastDepartureDate }
  }, [variation])

  const hasOneDeparture = firstDepartureDate === lastDepartureDate

  const tripItineraryTitle = intl.formatMessage({
    id: '0uz301HUAiu7olrXqYvy',
    defaultMessage: 'Trip itinerary',
    description: 'Modal title for tour trip itinerary on the tour offer detail page',
  })
  const viewOfferLabel = intl.formatMessage({
    id: 'ykK5S3C9-xgFqHVwVhNi',
    defaultMessage: 'View offer',
    description: 'Button text to view the tour offer details on the tour page',
  })
  const viewItineraryLabel = intl.formatMessage({
    id: 'a1cSvxR7u180Bo0aOzVB',
    defaultMessage: 'View itinerary',
    description: 'Link text to open tour itinerary modal on the tour offer detail page',
  })

  const openItineraryModal = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    showModal(<Modal
      title={tripItineraryTitle}
      primaryActionText={viewOfferLabel}
      primaryActionTo={offerUrl}
    >
      <TourV2Itinerary
        itinerary={variation!.itinerary}
        offerType={offer.type}
        showPrint
        showExperiences={false}
      />
    </Modal>)
  }, [showModal, variation, offer, offerUrl, tripItineraryTitle, viewOfferLabel])

  const cheapestPurchasableOption = useMemo(() => findCheapestTourV2PurchasableOption(offer), [offer])

  const pointsEarnCalculationOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      const daysCount = variation ? getTourV2VariationDuration(variation) : 0
      const nights = daysCount > 0 ? daysCount - 1 : undefined
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestPurchasableOption?.price,
            memberPrice: cheapestPurchasableOption?.memberPrice,
            roomType: cheapestPurchasableOption?.roomType,
            packageId: cheapestPurchasableOption?.fkDepartureId,
            offerId: cheapestPurchasableOption?.fkTourId,
            nights,
          }),
      ]
    },
    [offer, variation, cheapestPurchasableOption],
  )

  const isLuxPlusToursEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const loyaltyProductType = useMemo(() => getLoyaltyProductType(offer), [offer])

  const roomTypeCapacity = getRoomTypeCapacity(cheapestPurchasableOption?.roomType)
  const totalPrice = (cheapestPurchasableOption?.price ?? 0) * roomTypeCapacity
  const totalMemberPrice = (cheapestPurchasableOption?.memberPrice ?? 0) * roomTypeCapacity

  const tileBurn = useSearchOfferBurnPricing({
    price: totalPrice,
    memberPrice: totalMemberPrice,
    loyaltyProductType,
    viewPriceInPoints: filters?.viewPriceInPoints,
    disabled: !totalPrice,
    isLuxPlusEnabled: isLuxPlusToursEnabled,
  })

  return <Group direction="vertical" gap={4}>
    <Group direction="vertical" gap={8} noDisplayWhenEmpty>
      <Group direction="vertical" horizontalAlign="start" noDisplayWhenEmpty>
        <LoyaltyChooseDoubleRewardsOfferPill offer={offer} />
      </Group>
      <LoyaltyPointsEarn
        calculationRequests={pointsEarnCalculationOptions}
        calculationType="estimate"
        pointsToApply={tileBurn.pointsToApplyForEarn}
        burnPriceInPoints={tileBurn.priceInPointsForEarn}
        offer={offer}
      />
    </Group>
    <BodyText variant="medium" colour="light-foreground-alt" weight="bold">{getTourV2OperatorDisplayName(offer.operatedBy, isRebrandEnabled)}</BodyText>
    {variation && <Group direction="horizontal" gap="4 8" wrap="wrap">
      <BodyText variant="medium">{list.join(formatMiddleDotSeparator(intl))}</BodyText>

      {!!variation.itinerary.length && <TextLink onClick={openItineraryModal} size="medium">{viewItineraryLabel}</TextLink>}
      <BodyText variant="medium">
        {hasOneDeparture && <FormattedMessage
          id="XI9E_droPiZawKhwUKM9"
          defaultMessage="Travel on <bold>{departureDate}</bold>"
          description="Tour travel date when there is a single departure date"
          values={{
            bold: (chunks) => <b>{chunks}</b>,
            departureDate: <FormatDateTime value={firstDepartureDate} format="short-month-long-year" />,
          }}
        />}
        {!hasOneDeparture && <FormattedMessage
          id="Rbv51b5REYzRXswLgaxv"
          defaultMessage="Travel from <bold>{startDate}</bold> to <bold>{endDate}</bold>"
          description="Tour travel date range showing first and last departure dates"
          values={{
            bold: (chunks) => <b>{chunks}</b>,
            startDate: <FormatDateTime value={firstDepartureDate} format="short-month-long-year" />,
            endDate: <FormatDateTime value={lastDepartureDate} format="short-month-long-year" />,
          }}
        />}
      </BodyText>
    </Group>}
  </Group>
}

export default TourV2SearchTileMeta
